import React from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import '../../../assets/css/bookingHub.css';

const LocationCard = ({ loc, gid }) => {
  const history = useHistory();
  
  if (!loc) return null;

  const color = loc.colorCode || '#B85C00';
  const getContrastYIQ = (hexcolor) => {
    if (!hexcolor) return '#ffffff';
    const hex = hexcolor.replace('#', '');
    if (hex.length !== 6 && hex.length !== 3) return '#ffffff';
    
    let r, g, b;
    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      r = parseInt(hex.substring(0, 1) + hex.substring(0, 1), 16);
      g = parseInt(hex.substring(1, 1) + hex.substring(1, 1), 16);
      b = parseInt(hex.substring(2, 2) + hex.substring(2, 2), 16);
    }
    
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '#000000' : '#ffffff';
  };

  // Derive a tag color from the colorCode for the badge
  const tagBg = color;

  const formatAddress = (addr) => {
    if (!addr) return null;
    if (typeof addr === 'string') return addr;
    const parts = [addr.street, addr.city, addr.state, addr.postalCode].filter(Boolean);
    return parts.join(', ');
  };

  const addressStr = formatAddress(loc.address);
  // Use loc.gid as the locationGid for routing
  const locationId = loc.gid || loc.bookingHubGid;

  const getOperatingHoursText = () => {
    if (!loc.operatingHours || loc.operatingHours.length === 0) {
      return loc.timing || '6:00 AM – 6:00 PM';
    }

    const todayDayName = moment().format('dddd').toUpperCase();
    const todayHours = loc.operatingHours.find(h => h.dayOfWeek === todayDayName);
    const slots = todayHours?.timeSlots || todayHours?.timeSlot;
    
    const openSlots = slots?.filter(s => s.isOpen && s.openingTime !== undefined && s.openingTime !== null && s.openingTime !== '' && s.closingTime !== undefined && s.closingTime !== null && s.closingTime !== '') || [];
    
    if (openSlots.length > 0) {
      const formatTimeLabel = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMins = mins.toString().padStart(2, '0');
        return `${displayHours}:${displayMins} ${ampm}`;
      };

      const slotStrings = openSlots.map(slot => {
        return `${formatTimeLabel(slot.openingTime)} - ${formatTimeLabel(slot.closingTime)}`;
      });
      return `Today: ${slotStrings.join(', ')}`;
    }
    
    return 'Today closed. Check other dates';
  };

  return (
    <div className="bh-card">
      <div
        className="bh-card-image"
        style={{ backgroundImage: `url(${loc.bannerImages?.[0]})`, cursor: 'pointer' }}
        onClick={() => history.push(`/booking-hub/${gid}/location/${locationId}`, { fromListing: true })}
      >
        <div className="bh-card-image-overlay" />
        {/* Floating dynasty/type badge */}
        {loc.highlights?.[0] && (
          <div className="bh-card-badge" style={{ backgroundColor: tagBg, color: getContrastYIQ(color) }}>
            {loc.highlights[0]}
          </div>
        )}
        {loc.tags && loc.tags.length > 0 && (
          <div className="bh-card-tags-overlay" style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            gap: '6px',
            zIndex: 2
          }}>
            {loc.tags.map((tag, idx) => {
              const tagName = typeof tag === 'string' ? tag : tag?.name;
              if (!tagName) return null;
              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: color,
                    border: `1px solid ${color}`,
                    fontSize: '10px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  {tagName}
                </div>
              );
            })}
          </div>
        )}
        <div className="bh-card-image-footer">
          <div className="bh-card-image-name">{loc.landmark || loc.name}</div>
          {loc.landmark && (
            <div className="bh-card-image-local">{loc.name}</div>
          )}
        </div>
      </div>

      {/* Content section */}
      <div className="bh-card-body">
        <p className="bh-card-desc">{loc.description}</p>

        <div className="bh-card-meta">
          <div className="bh-card-meta-row">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <circle cx="5.5" cy="5.5" r="4.583" stroke={color} strokeWidth="0.917" strokeLinecap="round"/>
              <path d="M5.5 3.667V5.5l1.833 1.833" stroke={color} strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{getOperatingHoursText()}</span>
          </div>
          <div className="bh-card-meta-row" style={{ marginTop: '6px' }}>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path d="M5.5 10.083S1.833 6.875 1.833 4.125a3.667 3.667 0 0 1 7.334 0c0 2.75-3.667 5.958-3.667 5.958z" stroke={color} strokeWidth="0.917" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="5.5" cy="4.125" r="0.917" stroke={color} strokeWidth="0.917"/>
            </svg>
            <span>{addressStr || 'Address not available'}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="bh-card-actions">
          <button
            className="bh-card-btn-primary"
            style={{ backgroundColor: color, boxShadow: `0 2px 8px ${color}35` }}
            onClick={(e) => { e.stopPropagation(); history.push(`/booking-hub/${gid}/location/${locationId}/next`); }}
          >
            BOOK NOW
          </button>
          <button
            className="bh-card-btn-secondary"
            onClick={(e) => { e.stopPropagation(); history.push(`/booking-hub/${gid}/location/${locationId}`, { fromListing: true }); }}
          >
            VIEW DETAILS
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.875 5h6.25M5.833 2.917L8.125 5l-2.292 2.083" stroke="#6b5a47" strokeWidth="0.833" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationCard;
